import { ISubscriptionData } from "../../../interfaces/response/subscriptionData"
import { TCallbackFunction } from "../../../interfaces/common";
import { useAuthStore } from "../../../stores/auth/useAuthStore"
import { useBillingStore } from "../../../stores/billing/useBillingStore"
import { IResponse, useAxios } from "../useAxios"
import { ICancelationFlowQuestions } from "../../../components/modal/modals/cancelation-flow/CancelFlow";

export interface IBillingRequestParams {
    clb?: TCallbackFunction<IResponse>,
    errorClb?: TCallbackFunction<any>
}

export interface IBuySubscriptionParams {
    paymentMethodId: string,
    billingEmail?: string,
    billingName?: string,
    customerId?: string,
    currency?: string,
    licenceQuantity?: string
    priceId?: string
    couponId?: string
    prorationDate?: string
}

export interface IResumeSubscriptionParams {
    resumePause?: boolean,
    resumeCancel?: boolean
}

export interface IUpcomingPaymentParams {
    priceId: string,
    quantity: number,
    currency?: string,
    couponId?: string,
}

export const useBillingRequest = () => {
    const { sendRequest } = useAxios()
    const { store: { authData: { accessToken, user } } } = useAuthStore()
    const { store: { selectedBillingPlan, subscriptionId, appliedCoupon, setupIntent, upcomingPayment, licenceQuantity } } = useBillingStore()
    const route = '/billing/v2'

    //GET
    const getBillingPlansRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'billingPlans',
            url: `${route}/plans`,
            method: 'GET',
            ...params
        })
    }

    const getSetupIntentRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'setupIntent',
            url: `${route}/setup`,
            method: 'GET',
            ...params
        })
    }
    const calculateUpcomingPaymentRequestHandler = (params: IUpcomingPaymentParams) => {
        return sendRequest({
            slug: 'calculateUpcomingPayment',
            url: `${route}/subscription/upcoming`,
            method: 'GET',
            params
        })
    }
    const getInvoicesRequestHandler = (invoiceId?: string, params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'invoices',
            url: `${route}/invoices`,
            method: 'GET',
            params: {
                limit: 20,
                ...invoiceId && { startingAfterId: invoiceId }
            },
            ...params
        })
    }
    const getCurrentPayingPriceRequestHandler = (priceId?: string) => {
        return sendRequest({
            enabled: !!priceId,
            slug: 'currentPrice',
            url: `${route}/prices/${priceId}`,
            method: 'GET'
        })
    }
    const getBillingPlansFeaturesRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'plansFeatures',
            url: `${route}/features`,
            method: 'GET',
            ...params
        })
    }
    //PUT
    const selectSubscriptionRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!accessToken && !!selectedBillingPlan,
            slug: 'billingSubscription',
            url: `${route}/subscription`,
            method: 'PUT',
            payload: {
                priceId: selectedBillingPlan?.priceId,
                quantity: 1,
                //couponId: //todo: add coupon
            },
            ...params
        })
    }
    const buySubscriptionRequestHandler = (buySubscriptionParams: IBuySubscriptionParams, params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!buySubscriptionParams.paymentMethodId,
            slug: 'buySubscription',
            url: `${route}/subscription`,
            method: 'PUT',
            payload: {
                priceId: buySubscriptionParams.priceId ?? selectedBillingPlan?.priceId,
                paymentMethodId: buySubscriptionParams.paymentMethodId,
                quantity: buySubscriptionParams.licenceQuantity ? +buySubscriptionParams.licenceQuantity : licenceQuantity,
                couponId: buySubscriptionParams.couponId ?? appliedCoupon?.id,
                customerId: buySubscriptionParams.customerId ?? setupIntent?.customer,
                context: "workspace",//todo: workspace
                ...buySubscriptionParams.prorationDate && { prorationDate: +buySubscriptionParams.prorationDate },
                ...buySubscriptionParams.currency && { currency: buySubscriptionParams.currency }
            },
            ...params
        })
    }
    const confirmPaymentRequestHandler = (paymentMethodId: string, params?: IBillingRequestParams) => {//todo: remove
        return sendRequest({
            enabled: !!subscriptionId,
            slug: 'confirmPayment',
            url: `${route}/subscription/confirmation`,
            method: 'PUT',
            payload: {
                subscriptionId: subscriptionId,
            },
            ...params
        })
    }
    const applyCouponRequestHandler = (couponCode: string, params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!couponCode,
            slug: 'applyCoupon',
            url: `${route}/promotion`,
            method: 'PUT',
            payload: {
                code: couponCode,
            },
            ...params
        })
    }
    const pauseSubscriptionRequestHandler = (resumesAt: number, params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'pauseSubscription',
            url: `${route}/subscription/pause`,
            method: 'PUT',
            payload: {
                resumesAt: resumesAt,
            },
            ...params
        })
    }
    const resumeSubscriptionRequestHandler = (resume?: IResumeSubscriptionParams, params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'resumeSubscription',
            url: `${route}/subscription/resume`,
            method: 'PUT',
            payload: {
                resumePause: resume?.resumePause,
                resumeCancel: resume?.resumeCancel,
                isDefault: true
            },
            ...params
        })
    }
    //POST
    const setDefaultPaymentMethodRequestHandler = (paymentMethodId: string, params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!paymentMethodId,
            slug: 'setDefaultPaymentMethod',
            url: `${route}/payment-methods/default`,
            method: 'PUT',
            payload: { paymentMethodId },
            ...params
        })
    }

    const addPaymentMethodRequestHandler = (paymentMethodId: string, attachToCustomer?: boolean, params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!paymentMethodId,
            slug: 'addPaymentMethod',
            url: `${route}/payment-methods`,
            method: 'POST',
            payload: {
                paymentMethodId,
                isDefault: true,
                ...attachToCustomer && { attachToCustomer }
            },
            ...params,
            displayToast: false
        })
    }

    const updatePaymentIntentWithPaymentMethod = (paymentIntentId: string, paymentMethodId: string, calculatedPrice: any, oldClientSecret?: string, credits?: number, params?: IBillingRequestParams) => {
        return sendRequest({
            enabled: !!paymentMethodId,
            slug: 'updatePaymentIntentWithPaymentMethod',
            url: `${route}/payment-methods/update-intent`,
            method: 'POST',
            payload: {
                paymentIntentId,
                paymentMethodId,
                calculatedPrice,
                ...oldClientSecret && { oldClientSecret },
                ...credits && { credits }
            },
            ...params,
            displayToast: false
        })
    }

    const payInvoiceRequest = (invoiceId: string, paymentMethodId: string, params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'payInvoice',
            url: `${route}/pay-invoice`,
            method: 'POST',
            payload: {
                invoiceId,
                paymentMethodId
            },
            ...params,
            displayToast: false
        })
    }

    const voidInvoiceRequest = (invoiceId: string, params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'voidInvoice',
            url: `${route}/void-invoice`,
            method: 'POST',
            payload: {
                invoiceId,
            },
            ...params,
            displayToast: false
        })
    }

    // Payment Verification
    const getPaymentVerificationStatusRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'paymentVerificationStatus',
            url: `${route}/payment-verification/status`,
            method: 'GET',
            ...params
        })
    }

    // Resolve a fresh, confirmable PaymentIntent for the outstanding invoice. The stored client secret is a stale snapshot and must not be reused.
    const getPaymentVerificationIntentRequestHandler = (params?: IBillingRequestParams) => {
        return sendRequest({
            slug: 'paymentVerificationIntent',
            url: `${route}/payment-verification/intent`,
            method: 'POST',
            displayToast: false,
            ...params
        })
    }

    //DELETE
    const deleteSubscriptionRequestHandler = (cancelationFlowQuestions?: ICancelationFlowQuestions, params?: IBillingRequestParams) => {
        console.log(cancelationFlowQuestions)
        return sendRequest({
            slug: 'cancelSubscription',
            url: `${route}/subscription`,
            method: 'DELETE',
            payload: {
                reason: cancelationFlowQuestions
            },
            ...params
        })
    }
    return {
        getBillingPlansRequestHandler,
        getSetupIntentRequestHandler,
        calculateUpcomingPaymentRequestHandler,
        selectSubscriptionRequestHandler,
        buySubscriptionRequestHandler,
        confirmPaymentRequestHandler,
        applyCouponRequestHandler,
        getInvoicesRequestHandler,
        addPaymentMethodRequestHandler,
        pauseSubscriptionRequestHandler,
        resumeSubscriptionRequestHandler,
        deleteSubscriptionRequestHandler,
        getCurrentPayingPriceRequestHandler,
        getBillingPlansFeaturesRequestHandler,
        updatePaymentIntentWithPaymentMethod,
        payInvoiceRequest,
        voidInvoiceRequest,
        getPaymentVerificationStatusRequestHandler,
        getPaymentVerificationIntentRequestHandler,
        setDefaultPaymentMethodRequestHandler
    }

}